import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { IndianRupee, DollarSign, Users, Repeat, ShoppingBag, Activity } from "lucide-react";
import { Section, Spinner } from "./AiHtml";
import { money } from "./BuyPlanDialog";
import { api } from "../lib/api";

const Kpi = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="card-n p-4" data-testid={testId}>
    <div className="flex items-center justify-between"><p className="label-mono">{label}</p><Icon size={15} className="text-slate-500" /></div>
    <p className="font-display text-2xl font-bold mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

export const AdminOverview = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner label="Loading business metrics..." />;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={IndianRupee} label="Revenue (INR)" value={money(d.revenue.INR, "INR")} sub={`${money(d.revenueThisMonth.INR, "INR")} this month`} testId="kpi-revenue-inr" />
        <Kpi icon={DollarSign} label="Revenue (USD)" value={money(d.revenue.USD, "USD")} sub={`${money(d.revenueThisMonth.USD, "USD")} this month`} testId="kpi-revenue-usd" />
        <Kpi icon={Repeat} label="Active subscriptions" value={d.activeSubscriptions} sub={`${d.payingCustomers} paying customers`} testId="kpi-active-subs" />
        <Kpi icon={Users} label="Sub-accounts" value={d.users} sub={`${d.signupsThisMonth} new this month`} testId="kpi-users" />
        <Kpi icon={ShoppingBag} label="Purchases" value={d.purchases} testId="kpi-purchases" />
        <Kpi icon={Activity} label="Credits used (month)" value={d.creditsUsedThisMonth.toLocaleString()} sub="excludes admin demo usage" testId="kpi-credits-used" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Revenue · last 30 days">
          {d.daily.length === 0 ? <p className="text-sm text-slate-500">No sales in the last 30 days.</p> : (
            <div className="h-60"><ResponsiveContainer><BarChart data={d.daily}><CartesianGrid stroke="#1E293B" vertical={false} /><XAxis dataKey="date" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} /><Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #1E293B" }} /><Legend /><Bar dataKey="INR" fill="#FF6B00" radius={[4, 4, 0, 0]} /><Bar dataKey="USD" fill="#38BDF8" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          )}
        </Section>
        <Section title="Sales by plan">
          <table className="w-full text-sm"><thead><tr className="text-left text-xs font-mono uppercase text-slate-500"><th className="py-2">Plan</th><th>Sales</th><th>INR</th><th>USD</th></tr></thead>
            <tbody>{d.byPlan.map((p) => <tr key={p.plan} className="border-t border-slate-800"><td className="py-2">{p.plan}</td><td>{p.count}</td><td className="font-mono">{money(p.INR, "INR")}</td><td className="font-mono">{money(p.USD, "USD")}</td></tr>)}
              {d.byPlan.length === 0 && <tr><td colSpan={4} className="py-4 text-slate-500">No sales yet.</td></tr>}</tbody></table>
        </Section>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Top users by credits">
          <ul className="space-y-2 text-sm">{d.topUsers.map((u) => <li key={u.email} className="flex justify-between"><span className="truncate">{u.email}</span><span className="font-mono text-xs text-slate-400">{u.credits} cr · {u.tasks} tasks</span></li>)}</ul>
        </Section>
        <Section title="Recent purchases">
          <ul className="space-y-2 text-sm">{d.recentPurchases.map((p) => <li key={p.requestId} className="flex justify-between gap-2"><span className="truncate">{p.email} · {p.plan} {p.billing || ""}</span><span className="font-mono text-xs text-emerald-400">{money(p.amount, p.currency || "INR")}</span></li>)}
            {d.recentPurchases.length === 0 && <li className="text-slate-500">No purchases yet.</li>}</ul>
        </Section>
      </div>
    </div>
  );
};
